import { memo } from 'react';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction, useTranslation } from '@lyearn/i18n';
import Form, {
  FieldOverrides,
  Fields,
  FieldType,
  FormRefType,
  SectionLayout,
} from '@/components/Form';
import AttachGoalsContent from '@/modules/goals/modules/okr/views/OKRDetails/components/AttachGoals/AttachGoalContent';
import AttachSkillsContent from '@/modules/goals/modules/okr/views/OKRDetails/components/AttachSkills/AttachSkillsContent';
import { SkillTypeEnum } from '@/types/schema';

const OVERRIDES: FieldOverrides = {
  Section: {
    spacing: 'space-y-32',
  },
};

interface AttachMindsetsGoalsSkillsFormProps {
  initialValues: AttachMindsetsGoalsSkillsFormValues;
  formRef?: FormRefType<AttachMindsetsGoalsSkillsFormValues>;
  onSubmit: (data: AttachMindsetsGoalsSkillsFormValues) => void;
}

export interface AttachMindsetsGoalsSkillsFormValues {
  mindsetIds: string[];
  goalIds: string[];
  skillIds: string[];
}

const getFormFields = (): Fields<AttachMindsetsGoalsSkillsFormValues> => {
  return {
    mindsetIds: {
      name: 'mindsetIds',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <AttachSkillsContent
          selectedSkillIds={value ?? []}
          showConfirmBeforeAdd={false}
          showConfirmBeforeRemove={false}
          skillType={SkillTypeEnum.Behavior}
          onChange={onChange}
        />
      ),
    },
    goalIds: {
      name: 'goalIds',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <AttachGoalsContent
          selectedGoalIds={value ?? []}
          showConfirmBeforeAdd={false}
          showConfirmBeforeRemove={false}
          onChange={onChange}
        />
      ),
    },
    skillIds: {
      name: 'skillIds',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <AttachSkillsContent
          selectedSkillIds={value ?? []}
          showConfirmBeforeAdd={false}
          showConfirmBeforeRemove={false}
          skillType={SkillTypeEnum.Ability}
          onChange={onChange}
        />
      ),
    },
  };
};

const getFormSectionLayout = ({ t }: { t: TFunction }): SectionLayout =>
  getEnabledItems([
    {
      id: 'mindsets',
      name: t('Common:Form.Mindsets'),
      layout: ['mindsetIds'],
      enabled: true,
    },
    {
      id: 'goals',
      name: t('Common:Form.Goals'),
      layout: ['goalIds'],
      enabled: true,
    },
    {
      id: 'skills',
      name: t('Common:Form.Skills'),
      layout: ['skillIds'],
      enabled: true,
    },
  ]);

const AttachMindsetsGoalsSkillsForm: React.FC<AttachMindsetsGoalsSkillsFormProps> = (props) => {
  const { initialValues, onSubmit, formRef } = props;
  const { t } = useTranslation();

  const onSubmitHandler = (data: AttachMindsetsGoalsSkillsFormValues) => {
    onSubmit({
      mindsetIds: data.mindsetIds ?? [],
      goalIds: data.goalIds ?? [],
      skillIds: data.skillIds ?? [],
    });
  };

  return (
    <Form<AttachMindsetsGoalsSkillsFormValues>
      fields={getFormFields()}
      formName="AttachMindsetsGoalsSkillsForm"
      formRef={formRef}
      initialValues={initialValues}
      overrides={OVERRIDES}
      sectionLayout={getFormSectionLayout({ t })}
      onSubmit={onSubmitHandler}
    />
  );
};

export default memo(AttachMindsetsGoalsSkillsForm);
